// ============================================================
// Page Inventaires — Lot L15 du plan SaaS (frontend)
// ============================================================
// Workflow inventaire physique :
//   1. Créer (statut=en_cours, boutique choisie)
//   2. Saisir le comptage de chaque variante (quantite_comptee)
//   3. Soumettre (statut=soumis) — l'écart est calculé par le backend
//   4. Le propriétaire valide (statut=valide) → mouvements de correction
//      OU rejette (statut=rejete)
// ============================================================

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  ClipboardList, Plus, X, Loader, CheckCircle2, Send, XCircle,
  Eye, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUTS = {
  en_cours:  { label: 'En cours',  color: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  soumis:    { label: 'Soumis',    color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  valide:    { label: 'Validé',    color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  rejete:    { label: 'Rejeté',    color: 'bg-red-500/15 text-red-300 border-red-500/30' },
};

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('fr-FR') : '—'; }

function ModalCreer({ onClose, onSuccess }) {
  const [boutiques, setBoutiques] = useState([]);
  const [form, setForm] = useState({ boutique_id: '', libelle: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/boutiques').then(r => setBoutiques(r.data || [])).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.boutique_id) { toast.error('Choisis une boutique'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/inventaires', form);
      toast.success('Inventaire créé');
      onSuccess(data.inventaire?.id || data.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Nouvel inventaire</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Boutique *</label>
            <select value={form.boutique_id} onChange={e => setForm({ ...form, boutique_id: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
              <option value="">—</option>
              {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Libellé</label>
            <input type="text" value={form.libelle} onChange={e => setForm({ ...form, libelle: e.target.value })}
              placeholder="Ex: Inventaire mensuel mai"
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 rounded-lg">Annuler</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 disabled:opacity-40 text-gray-900 text-sm font-medium rounded-lg flex items-center gap-2">
            {saving ? <Loader size={14} className="animate-spin" /> : <ClipboardList size={14} />} Créer
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalDetail({ id, onClose, onChange }) {
  const { canEditParametres } = useAuth();  // valider = writeOwner
  const [data, setData] = useState(null);
  const [variantes, setVariantes] = useState([]);
  const [nouvelleLigne, setNouvelleLigne] = useState({ variante_id: '', quantite_comptee: 0 });

  const charger = () => api.get(`/inventaires/${id}`).then(r => setData(r.data));

  useEffect(() => {
    charger();
    api.get('/produits/variantes').then(r => setVariantes(r.data || []));
  }, [id]);  // eslint-disable-line

  if (!data) return null;
  const { inventaire, lignes = [] } = data;
  const st = STATUTS[inventaire.statut] || STATUTS.en_cours;
  const editable = inventaire.statut === 'en_cours';

  const ajouterLigne = async () => {
    if (!nouvelleLigne.variante_id) { toast.error('Choisis une variante'); return; }
    try {
      await api.post(`/inventaires/${id}/ligne`, {
        variante_id: nouvelleLigne.variante_id,
        quantite_comptee: parseInt(nouvelleLigne.quantite_comptee) || 0,
      });
      setNouvelleLigne({ variante_id: '', quantite_comptee: 0 });
      charger();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };

  const retirerLigne = async (varianteId) => {
    if (!window.confirm('Retirer cette ligne ?')) return;
    try {
      await api.delete(`/inventaires/${id}/ligne/${varianteId}`);
      charger();
    } catch (err) { toast.error('Échec'); }
  };

  const soumettre = async () => {
    if (!window.confirm('Soumettre l\'inventaire pour validation ?')) return;
    try {
      await api.patch(`/inventaires/${id}/soumettre`);
      toast.success('Soumis');
      charger(); onChange();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };
  const valider = async () => {
    if (!window.confirm('Valider l\'inventaire ? Les écarts seront appliqués au stock.')) return;
    try {
      await api.patch(`/inventaires/${id}/valider`);
      toast.success('Validé');
      charger(); onChange();
    } catch (err) { toast.error(err.response?.data?.message || 'Échec'); }
  };
  const rejeter = async () => {
    const motif = window.prompt('Motif du rejet :');
    if (!motif?.trim()) return;
    try {
      await api.patch(`/inventaires/${id}/rejeter`, { motif });
      toast.success('Rejeté');
      charger(); onChange();
    } catch (err) { toast.error('Échec'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="bg-gray-900 border border-gray-700 rounded-xl max-w-4xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-white">{inventaire.libelle || `Inventaire ${inventaire.id?.substring(0, 8)}`}</h3>
            <p className="text-xs text-gray-500">Boutique {inventaire.boutique_nom} · {fmtDate(inventaire.created_at)}</p>
          </div>
          <span className={`px-2 py-1 rounded border text-xs ${st.color}`}>{st.label}</span>
        </div>

        {editable && (
          <div className="bg-gray-800/40 rounded p-3 mb-3">
            <p className="text-xs text-gray-400 mb-2">Ajouter une ligne</p>
            <div className="flex gap-2">
              <select value={nouvelleLigne.variante_id}
                onChange={e => setNouvelleLigne({ ...nouvelleLigne, variante_id: e.target.value })}
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="">— Variante —</option>
                {variantes.map(v => (
                  <option key={v.id} value={v.id}>
                    {v.produit_nom} · {v.modele || v.code_variante}{v.couleur ? ` · ${v.couleur}` : ''}{v.taille_nom ? ` · ${v.taille_nom}` : ''}
                  </option>
                ))}
              </select>
              <input type="number" min="0" placeholder="Qté comptée"
                value={nouvelleLigne.quantite_comptee}
                onChange={e => setNouvelleLigne({ ...nouvelleLigne, quantite_comptee: e.target.value })}
                className="w-32 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white text-right tabular-nums" />
              <button onClick={ajouterLigne}
                className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded flex items-center gap-1">
                <Plus size={14} /> Ajouter
              </button>
            </div>
          </div>
        )}

        <table className="w-full text-sm border border-gray-800 rounded mb-3">
          <thead className="bg-gray-800/50">
            <tr className="text-xs text-gray-400 text-left">
              <th className="p-1">Variante</th>
              <th className="p-1 text-right">Stock théorique</th>
              <th className="p-1 text-right">Compté</th>
              <th className="p-1 text-right">Écart</th>
              {editable && <th className="p-1"></th>}
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 ? (
              <tr><td colSpan={editable ? 5 : 4} className="p-3 text-center text-gray-500">Aucune ligne</td></tr>
            ) : lignes.map((l, i) => {
              const ecart = (l.quantite_comptee || 0) - (l.stock_theo || 0);
              return (
                <tr key={i} className="border-t border-gray-800">
                  <td className="p-1 text-xs text-gray-300">{l.produit_nom} {l.modele || l.code_variante}</td>
                  <td className="p-1 text-right text-gray-400 tabular-nums">{l.stock_theo}</td>
                  <td className="p-1 text-right text-white tabular-nums">{l.quantite_comptee}</td>
                  <td className={`p-1 text-right tabular-nums ${ecart < 0 ? 'text-red-400' : ecart > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>
                    {ecart > 0 ? '+' : ''}{ecart}
                  </td>
                  {editable && (
                    <td className="p-1 text-right">
                      <button onClick={() => retirerLigne(l.variante_id)} className="text-gray-500 hover:text-red-400">
                        <Trash2 size={12} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-800">
          {editable && lignes.length > 0 && (
            <button onClick={soumettre}
              className="px-3 py-1.5 bg-amber-500/15 text-amber-300 text-sm rounded flex items-center gap-1">
              <Send size={14} /> Soumettre
            </button>
          )}
          {inventaire.statut === 'soumis' && canEditParametres && (
            <>
              <button onClick={rejeter}
                className="px-3 py-1.5 bg-red-500/15 text-red-300 text-sm rounded flex items-center gap-1">
                <XCircle size={14} /> Rejeter
              </button>
              <button onClick={valider}
                className="px-3 py-1.5 bg-emerald-500/15 text-emerald-300 text-sm rounded flex items-center gap-1">
                <CheckCircle2 size={14} /> Valider
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PageInventaires() {
  const { canEditBusiness } = useAuth();
  const [inventaires, setInventaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreer, setShowCreer] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statut, setStatut] = useState('');

  const charger = async () => {
    setLoading(true);
    try {
      const params = statut ? { statut } : {};
      const { data } = await api.get('/inventaires', { params });
      setInventaires(data.inventaires || data || []);
    } catch { toast.error('Échec'); } finally { setLoading(false); }
  };
  useEffect(() => { charger(); }, [statut]);  // eslint-disable-line

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
            <ClipboardList size={18} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Inventaires</h1>
            <p className="text-xs text-gray-500">Comptage physique par boutique avec validation</p>
          </div>
        </div>
        {canEditBusiness && (
          <button onClick={() => setShowCreer(true)}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 text-sm font-medium rounded-lg">
            <Plus size={14} /> Nouvel inventaire
          </button>
        )}
      </header>

      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
        <select value={statut} onChange={e => setStatut(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
          <option value="">Tous statuts</option>
          {Object.entries(STATUTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50">
            <tr className="text-left text-xs text-gray-400">
              <th className="px-3 py-2">Libellé</th>
              <th className="px-3 py-2">Boutique</th>
              <th className="px-3 py-2">Créé</th>
              <th className="px-3 py-2 text-right">Lignes</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8"><Loader size={20} className="animate-spin mx-auto" /></td></tr>
            ) : inventaires.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-gray-500 py-8">Aucun inventaire</td></tr>
            ) : inventaires.map(i => {
              const st = STATUTS[i.statut] || STATUTS.en_cours;
              return (
                <tr key={i.id} className="border-t border-gray-800 hover:bg-gray-800/30">
                  <td className="px-3 py-2 text-gray-300">{i.libelle || `Inv. ${i.id?.substring(0, 8)}`}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{i.boutique_nom}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(i.created_at)}</td>
                  <td className="px-3 py-2 text-right text-gray-300 tabular-nums">{i.nb_lignes || 0}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs border ${st.color}`}>{st.label}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setSelectedId(i.id)} className="text-gray-400 hover:text-white"><Eye size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreer && <ModalCreer onClose={() => setShowCreer(false)} onSuccess={(id) => { setShowCreer(false); charger(); setSelectedId(id); }} />}
      {selectedId && <ModalDetail id={selectedId} onClose={() => setSelectedId(null)} onChange={charger} />}
    </div>
  );
}
